import React, { useEffect, useRef, useState } from "react";
import { InputField } from "@jem-open/jem-ui";
import { Search } from "lucide-react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

export interface AddressSearchProps {
  /** Called with the resolved coordinates when a suggestion is chosen. */
  onSelect: (latitude: number, longitude: number) => void;
  label?: string;
  disabled?: boolean;
}

interface Suggestion {
  placeId: string;
  description: string;
}

/**
 * Address search backed by the Google Places API (New).
 *
 * Mirrors the proven typeahead in `time-attendance/pages/ShiftSiteConfigPage`,
 * rebuilt with jem-ui primitives: a debounced `InputField` plus a lightweight
 * suggestions list. Selecting a suggestion resolves the place's location and
 * reports it via `onSelect` — the address text itself is not persisted, it only
 * drives the latitude/longitude (and therefore the map + geofence).
 */
export const AddressSearch: React.FC<AddressSearchProps> = ({
  onSelect,
  label = "Address",
  disabled = false,
}) => {
  const placesLib = useMapsLibrary("places");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the suggestions list on outside click.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(
    () => (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const fetchSuggestions = async (input: string): Promise<void> => {
    if (!placesLib) return;
    try {
      const { suggestions } =
        await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
        });
      setItems(
        suggestions.map((s) => ({
          placeId: s.placePrediction?.placeId || "",
          description: s.placePrediction?.text?.text || "",
        }))
      );
      setOpen(true);
    } catch {
      setItems([]);
      setOpen(false);
    }
  };

  const handleChange = (value: string): void => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim() || !placesLib) {
      setItems([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSelect = async (
    placeId: string,
    description: string
  ): Promise<void> => {
    setQuery(description);
    setItems([]);
    setOpen(false);
    if (!placesLib || !placeId) return;
    try {
      const place = new placesLib.Place({ id: placeId });
      await place.fetchFields({ fields: ["location"] });
      if (place.location) {
        onSelect(place.location.lat(), place.location.lng());
      }
    } catch {
      // Place lookup failed — leave the coordinates unchanged.
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <InputField
        label={label}
        placeholder="Search for an address"
        icon={<Search size={14} aria-hidden="true" />}
        value={query}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          if (items.length > 0) setOpen(true);
        }}
      />
      {open && items.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-50 mt-xxs max-h-[200px] overflow-auto rounded-md border border-greyscale-border bg-white py-xxs shadow-md">
          {items.map((item) => (
            <li key={item.placeId}>
              <button
                type="button"
                className="block w-full cursor-pointer px-sm py-xs text-left text-sm text-greyscale-text-body hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none"
                onClick={() => handleSelect(item.placeId, item.description)}
              >
                {item.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressSearch;
